import { env } from '@/lib/env';
import { ApiError, type ProblemDetail } from '@/features/auth/api';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Command Center HTTP client (REST). The WebSocket bridge lives in
 * `ws.ts` and uses the same access token for query-string auth.
 */

export type AssetDto = {
  id: string;
  code: string;
  name: string;
  asset_type_slug: string;
  plate_number: string | null;
  photo_url: string | null;
  status: 'ACTIVE' | 'IDLE' | 'ALERT' | 'OFFLINE' | 'STALE' | 'MAINTENANCE';
  lat: number | null;
  lng: number | null;
  heading: number | null;
  speed_kmh: number | null;
  last_fix_at: string | null;
  assigned_field_user_id: string | null;
};

export type SnapshotDto = {
  generated_at: string;
  assets: AssetDto[];
};

export type TrailPointDto = {
  lat: number;
  lng: number;
  heading: number | null;
  speed_kmh: number | null;
  recorded_at: string;
};

export type TrailDto = {
  asset_id: string;
  zoom: number;
  tolerance_m: number;
  raw_count: number;
  decimated_count: number;
  encode: string;
  points: TrailPointDto[];
};

export type AlertSeverity = 'INFO' | 'WARNING' | 'CRITICAL';
export type AlertStatusKind = 'OPEN' | 'ACKNOWLEDGED' | 'RESOLVED';

export type AlertDto = {
  id: string;
  alert_type: string;
  severity: AlertSeverity;
  status: AlertStatusKind;
  title: string;
  message: string;
  asset_id: string | null;
  field_user_id: string | null;
  latitude: number | null;
  longitude: number | null;
  raised_at: string;
  acknowledged_at: string | null;
  acknowledged_by_user_id: string | null;
  resolved_at: string | null;
  resolved_by_user_id: string | null;
  resolution_note: string | null;
};

export type GeofenceDto = {
  id: string;
  name: string;
  kind: string;
  color: string;
  is_enabled: boolean;
  shape_type: 'Polygon' | 'Circle';
  geometry: Record<string, unknown>;
  rules: Record<string, unknown>;
  engagement_id: string | null;
};

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = useAuthStore.getState().accessToken;
  const url = `${env.VITE_API_URL}/api/v1${path}`;
  const headers = new Headers(init.headers ?? {});
  headers.set('Accept', 'application/json');
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(url, { ...init, headers });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const problem: ProblemDetail = (data?.error ?? data) || {
      detail: `HTTP ${res.status}`,
    };
    throw new ApiError(res.status, problem);
  }
  return data as T;
}

export const commandCenterApi = {
  snapshot: () => call<SnapshotDto>('/command-center/snapshot'),
  listAssets: () => call<AssetDto[]>('/command-center/assets'),
  assetTrail: (
    assetId: string,
    params: { start: string; end: string; zoom?: number; encode?: string },
  ) => {
    const q = new URLSearchParams({
      start: params.start,
      end: params.end,
      zoom: String(params.zoom ?? 14),
      encode: params.encode ?? 'chronological',
    });
    return call<TrailDto>(`/command-center/assets/${assetId}/trail?${q.toString()}`);
  },
  tenantTrails: (params: { start: string; end: string; zoom?: number }) => {
    const q = new URLSearchParams({
      start: params.start,
      end: params.end,
      zoom: String(params.zoom ?? 12),
    });
    return call<{ trails: Array<TrailDto> }>(`/command-center/trails?${q.toString()}`);
  },
  listAlerts: (
    params: {
      open_only?: boolean;
      types?: string[];
      severities?: AlertSeverity[];
      statuses?: AlertStatusKind[];
      limit?: number;
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.open_only) q.set('open_only', 'true');
    if (params.limit) q.set('limit', String(params.limit));
    for (const t of params.types ?? []) q.append('types', t);
    for (const s of params.severities ?? []) q.append('severities', s);
    for (const st of params.statuses ?? []) q.append('status', st);
    const qs = q.toString();
    return call<AlertDto[]>(`/command-center/alerts${qs ? `?${qs}` : ''}`);
  },
  acknowledgeAlert: (alertId: string, note?: string) =>
    call<AlertDto>(`/command-center/alerts/${alertId}/ack`, {
      method: 'POST',
      body: JSON.stringify({ note: note ?? null }),
    }),
  resolveAlert: (alertId: string, note?: string) =>
    call<AlertDto>(`/command-center/alerts/${alertId}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ note: note ?? null }),
    }),
  listGeofences: () => call<GeofenceDto[]>('/command-center/geofences'),
  createGeofence: (input: Omit<GeofenceDto, 'id'>) =>
    call<GeofenceDto>('/command-center/geofences', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  deleteGeofence: (id: string) =>
    call<void>(`/command-center/geofences/${id}`, { method: 'DELETE' }),
};
